import { calgaryCalendarDate } from '../../utils/calgaryTime';
import { useState, useMemo, useCallback } from "react";
import "./Expenses.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faArrowUpRightFromSquare, faArrowUp, faClock, faFileInvoice, faPenToSquare, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useQuery } from "@tanstack/react-query";
import VenExpService from "../../AppSettings/services/VenExpService";
import OppExpService from "../../AppSettings/services/OppExpService";
import GenExpensesService from "../../AppSettings/services/GenExpensesService";
import InvoiceService from "../../AppSettings/services/InvoiceService";
import { buildInvoiceNumberMap, resolveInvoiceNumber } from '../../helpers/invoiceNumber';
import { normalizeToArray } from '../../helpers/responseHelpers';
import { parseLocalDate, formatLocalDate } from '../../helpers/dateHelpers';
import FixBillService from "../../AppSettings/services/FixBillService";
import AgentsService from "../../AppSettings/services/AgentsService";
import Table from "../../Components/Table/Table";
import type { Column } from "@frontend/Models/Table/TableModels";
import LogExpenseEntryModal from "./Components/LogExpenseEntryModal/LogExpenseEntryModal";
import ExpensesExportModal from "./Components/ExpensesExportModal/ExpensesExportModal";
import ExpensesInvoicedPanel from "./Components/ExpensesInvoicedPanel/ExpensesInvoicedPanel";

interface ExpenseItem {
  type: string;
  id: number | string;
  rawId: string;
  amount: number;
  description: string;
  date: string;
  dateObj: Date;
  agent: string;
  agentId?: string;
  status: string;
  invoiceNumber: string;
  rawDateValue?: string;
  matterid?: string;
}

interface AgentMap {
  [key: string | number]: {
    name: string;
    company?: string;
  };
}

interface ExpenseMetrics {
  uninvoicedAmount: number;
  uninvoicedCount: number;
  totalExpenses: number;
  topCategory: string;
  invoicedAmount: number;
}

const Expenses: React.FC = () => {
  const [typeFilter, setTypeFilter] = useState<string>("All types");
  const [dateFilter, setDateFilter] = useState<string>("All time");
  const [invoiceFilter, setInvoiceFilter] = useState<"all" | "invoiced" | "uninvoiced">("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  const [showInvoicedPanel, setShowInvoicedPanel] = useState<boolean>(false);
  const [editingItem, setEditingItem] = useState<ExpenseItem | null>(null);
  const [deleteItem, setDeleteItem] = useState<ExpenseItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editFormData, setEditFormData] = useState<{
    amount: string; description: string; date: string;
  }>({ amount: "", description: "", date: "" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const { data: expensesQueryData, isPending: isLoading, refetch: refetchExpenses } = useQuery({
    queryKey: ['expenses-all'],
    queryFn: async () => {
      const agentsData = await AgentsService.getAgents();
      const agentsMap: AgentMap = Array.isArray(agentsData) ? (agentsData as any[]).reduce((acc: AgentMap, agent: any) => {
        acc[agent.AgentID] = {
          name: `${agent.FirstName} ${agent.LastName}`,
          company: agent.Company
        };
        return acc;
      }, {} as AgentMap) : {};

      const [vendorData, operatingData, generalDataRaw, fixedBillsData, invoicesData] = await Promise.all([
        VenExpService.getAllVenExpenses(),
        OppExpService.getAllOppExpenses(),
        GenExpensesService.getAllGenExpenses(),
        FixBillService.getAllFixBillExpenses(),
        InvoiceService.getAllInvoices().catch(() => [])
      ]);

      const generalData: any[] = normalizeToArray(generalDataRaw);
      const fixedBillsList = normalizeToArray(fixedBillsData);
      const invoiceNumberMap = buildInvoiceNumberMap(normalizeToArray(invoicesData));

      let invoicedItemCount = 0;
      invoicedItemCount += (vendorData || []).filter((item: any) => item.invoiceid || item.invoiced).length;
      invoicedItemCount += (operatingData || []).filter((item: any) => item.invoiceid || item.invoiced).length;
      invoicedItemCount += (generalData || []).filter((item: any) => item.invoiceid || item.invoiced).length;
      invoicedItemCount += fixedBillsList.filter((item: any) => item.invoiceid || item.invoiced).length;

      const getAgentLabel = (item: any) =>
        agentsMap[item.agentid]
          ? `${agentsMap[item.agentid].name}${agentsMap[item.agentid].company ? ` - ${agentsMap[item.agentid].company}` : ''}`
          : 'Unknown Agent';

      const invoiceTag = (item: any) => (item.invoiceid || item.invoiced) ? "Invoiced" : "Uninvoiced";

      const formattedVendorData: ExpenseItem[] = (vendorData || []).map((item: any) => ({
        type: "Vendor Services",
        id: `vendor-${item.ven_id}`,
        rawId: String(item.ven_id || ""),
        amount: Number.isFinite(parseFloat(item.amount)) ? parseFloat(item.amount) : 0,
        description: `${item.description || ""} · ${invoiceTag(item)}`,
        date: formatLocalDate(item.date_added),
        dateObj: parseLocalDate(item.date_added),
        agent: getAgentLabel(item),
        agentId: item.agentid ? String(item.agentid) : "",
        status: "Approved",
        invoiceNumber: item.invoiceid ? String(resolveInvoiceNumber(item.invoiceid, invoiceNumberMap)) : (item.invoiced ? String(item.invoiced) : ""),
        rawDateValue: item.date_added ? String(item.date_added).slice(0, 10) : "",
        matterid: item.matterid || "",
      }));

      const formattedOperatingData: ExpenseItem[] = (operatingData || []).map((item: any) => ({
        type: "Operating",
        id: `operating-${item.opp_id}`,
        rawId: String(item.opp_id || ""),
        amount: Number.isFinite(parseFloat(item.amount)) ? parseFloat(item.amount) : 0,
        description: `${item.description || ""} · ${invoiceTag(item)}`,
        date: formatLocalDate(item.date_added),
        dateObj: parseLocalDate(item.date_added),
        agent: getAgentLabel(item),
        agentId: item.agentid ? String(item.agentid) : "",
        status: "Approved",
        invoiceNumber: item.invoiceid ? String(resolveInvoiceNumber(item.invoiceid, invoiceNumberMap)) : (item.invoiced ? String(item.invoiced) : ""),
        rawDateValue: item.date_added ? String(item.date_added).slice(0, 10) : "",
        matterid: item.matterid || "",
      }));

      const formattedGeneralData: ExpenseItem[] = (generalData || []).map((item: any, idx: number) => ({
        type: "General",
        id: `general-${item.ge_id ?? item.gen_id ?? idx}`,
        rawId: String(item.ge_id ?? item.gen_id ?? ""),
        amount: Number.isFinite(parseFloat(item.amount)) ? parseFloat(item.amount) : 0,
        description: `${item.description || ""} · ${invoiceTag(item)}`,
        date: formatLocalDate(item.date_added),
        dateObj: parseLocalDate(item.date_added),
        agent: getAgentLabel(item),
        agentId: item.agentid ? String(item.agentid) : "",
        status: "Approved",
        invoiceNumber: item.invoiceid ? String(resolveInvoiceNumber(item.invoiceid, invoiceNumberMap)) : (item.invoiced ? String(item.invoiced) : ""),
        rawDateValue: item.date_added ? String(item.date_added).slice(0, 10) : "",
        matterid: item.matterid || "",
      }));

      const formattedFixedBillsData: ExpenseItem[] = fixedBillsList.map((item: any, idx: number) => ({
        type: "Fixed Bills",
        id: `fixedbills-${item.bill_id ?? idx}`,
        rawId: String(item.fbill_id || item.bill_id || ""),
        amount: Number.isFinite(parseFloat(item.amount)) ? parseFloat(item.amount) : 0,
        description: `${item.description || ""} · ${invoiceTag(item)}`,
        date: formatLocalDate(item.date_added),
        dateObj: parseLocalDate(item.date_added),
        agent: getAgentLabel(item),
        agentId: item.agentid ? String(item.agentid) : "",
        status: "Approved",
        invoiceNumber: item.invoiceid ? String(resolveInvoiceNumber(item.invoiceid, invoiceNumberMap)) : (item.invoiced ? String(item.invoiced) : ""),
        rawDateValue: item.date_added ? String(item.date_added).slice(0, 10) : "",
        matterid: item.matterid || "",
      }));

      const combinedData: ExpenseItem[] = [
        ...formattedVendorData,
        ...formattedOperatingData,
        ...formattedGeneralData,
        ...formattedFixedBillsData
      ].sort((a, b) => b.dateObj.getTime() - a.dateObj.getTime());

      return { items: combinedData, invoicedCount: invoicedItemCount };
    },
    staleTime: 2 * 60 * 1000,
  });

  const allExpensesData = expensesQueryData?.items ?? [];
  const invoicedCount = expensesQueryData?.invoicedCount ?? 0;

  const fetchAllExpensesData = useCallback(() => { refetchExpenses(); }, [refetchExpenses]);

  const filteredExpenses: ExpenseItem[] = useMemo(() => {
    let filtered = [...allExpensesData];

    if (typeFilter !== "All types") {
      filtered = filtered.filter(item => item.type === typeFilter);
    }

    const now = calgaryCalendarDate();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    if (dateFilter === "Last 7 Days") {
      const cutoff = todayStart - 7 * 24 * 60 * 60 * 1000;
      filtered = filtered.filter(item => item.dateObj.getTime() >= cutoff);
    } else if (dateFilter === "Last 90 Days") {
      const cutoff = todayStart - 90 * 24 * 60 * 60 * 1000;
      filtered = filtered.filter(item => item.dateObj.getTime() >= cutoff);
    } else if (dateFilter === "This Year") {
      const currentYear = now.getFullYear();
      filtered = filtered.filter(item => item.dateObj.getFullYear() === currentYear);
    }

    if (invoiceFilter === "invoiced") {
      filtered = filtered.filter(item => !!item.invoiceNumber);
    } else if (invoiceFilter === "uninvoiced") {
      filtered = filtered.filter(item => !item.invoiceNumber);
    }

    if (searchQuery) {
      filtered = filtered.filter(item =>
        item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.agent.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  }, [allExpensesData, typeFilter, dateFilter, invoiceFilter, searchQuery]);

  // Summary metrics derived from the FILTERED view so the cards match the table.
  const metrics: ExpenseMetrics = useMemo(() => {
    const totalExpenses = filteredExpenses.reduce((sum, item) => sum + item.amount, 0);

    const typeGroups: { [key: string]: number } = filteredExpenses.reduce((acc, item) => {
      acc[item.type] = (acc[item.type] || 0) + item.amount;
      return acc;
    }, {} as { [key: string]: number });
    const topCategory = Object.entries(typeGroups).sort((a, b) => b[1] - a[1])[0]?.[0] || "Office Supplies";

    const uninvoiced = filteredExpenses.filter(item => !item.invoiceNumber);
    const uninvoicedAmount = uninvoiced.reduce((sum, item) => sum + item.amount, 0);
    const uninvoicedCount = uninvoiced.length;
    const invoicedAmount = totalExpenses - uninvoicedAmount;

    return { uninvoicedAmount, uninvoicedCount, totalExpenses, topCategory, invoicedAmount };
  }, [filteredExpenses]);

  const totalFiltered = filteredExpenses.length;
  const pageLimit = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const paginatedExpenses = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredExpenses.slice(start, start + pageSize);
  }, [filteredExpenses, page, pageSize]);

  const handleExport = (): void => { setShowExportModal(true); };
  const handleAddTransaction = (): void => { setShowAddModal(true); };
  const handleModalSuccess = (): void => { fetchAllExpensesData(); };

  const openEditModal = useCallback((item: ExpenseItem) => {
    setEditingItem(item);
    setEditFormData({
      amount: item.amount.toFixed(2),
      description: item.description.replace(/ · (Invoiced|Uninvoiced)$/, ""),
      date: item.rawDateValue || "",
    });
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingItem) return;
    setIsSavingEdit(true);
    try {
      const { type, rawId } = editingItem;
      const payload: any = {
        description: editFormData.description,
        amount: editFormData.amount,
        date_added: editFormData.date || null,
        agentid: editingItem.agentId || "",
        matterid: editingItem.matterid || "",
      };
      if (type === "Vendor Services") {
        await VenExpService.updateVenExpense(rawId, payload);
      } else if (type === "Operating") {
        await OppExpService.updateOppExpense(rawId, payload);
      } else if (type === "General") {
        await GenExpensesService.updateGenExpense(rawId, payload);
      } else if (type === "Fixed Bills") {
        await FixBillService.updateFixBillExpense(rawId, payload);
      }
      setEditingItem(null);
      fetchAllExpensesData();
    } catch (error) {
      console.error("Error saving edit:", error);
    } finally {
      setIsSavingEdit(false);
    }
  }, [editingItem, editFormData, fetchAllExpensesData]);

  const handleDelete = useCallback(async () => {
    if (!deleteItem) return;
    setIsDeleting(true);
    try {
      const { type, rawId } = deleteItem;
      if (type === "Vendor Services") {
        await VenExpService.deleteVenExpense(rawId);
      } else if (type === "Operating") {
        await OppExpService.deleteOppExpense(rawId);
      } else if (type === "General") {
        await GenExpensesService.deleteGenExpense(rawId);
      } else if (type === "Fixed Bills") {
        await FixBillService.deleteFixBillExpense(rawId);
      }
      setDeleteItem(null);
      fetchAllExpensesData();
    } catch (error) {
      console.error("Error deleting item:", error);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteItem, fetchAllExpensesData]);

  const columns: Column<ExpenseItem>[] = [
    {
      header: "TYPE",
      render: (row: ExpenseItem) => (
        <span className="general-expense-type">{row.type}</span>
      ),
    },
    {
      header: "AMOUNT",
      render: (row: ExpenseItem) => (
        <span className="general-expense-amount">${row.amount.toFixed(2)}</span>
      ),
    },
    {
      header: "DESCRIPTION",
      accessor: "description",
      render: (row: ExpenseItem) => (
        <span className="general-expense-description">{row.description}</span>
      ),
    },
    {
      header: "DATE",
      accessor: "date",
      render: (row: ExpenseItem) => (
        <span className="general-expense-date">{row.date}</span>
      ),
    },
    {
      header: "AGENT",
      render: (row: ExpenseItem) => (
        <span className="general-expense-agent">{row.agent}</span>
      ),
    },
    {
      header: "ACTIONS",
      render: (row: ExpenseItem) => {
        const isInvoiced = !!row.invoiceNumber;
        return (
          <div className="general-billing-actions-cell" onClick={(e) => e.stopPropagation()}>
            <button
              className="general-billing-action-btn general-billing-edit-btn"
              onClick={() => openEditModal(row)}
              title="Edit"
              disabled={isInvoiced}
            >
              <FontAwesomeIcon icon={faPenToSquare} />
            </button>
            <button
              className="general-billing-action-btn general-billing-delete-btn"
              onClick={() => setDeleteItem(row)}
              title="Delete"
              disabled={isInvoiced}
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="general-expenses-page-container">
      <div className="general-expenses-header">
        <div>
          <h2>Expenses</h2>
          <p className="general-header-sub">Track all financials here.</p>
        </div>
        <div className="general-expenses-actions">
          <button className="general-expenses-ghost-btn" onClick={() => setShowInvoicedPanel(true)}>
            <FontAwesomeIcon icon={faFileInvoice} />
            <span>Already Invoiced</span>
            {invoicedCount > 0 && (
              <span className="general-expenses-badge">{invoicedCount}</span>
            )}
          </button>
          <button className="general-expenses-ghost-btn" onClick={handleExport}>
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
            <span>Export</span>
          </button>
          <button className="general-expenses-primary-btn" onClick={handleAddTransaction}>
            <FontAwesomeIcon icon={faPlus} />
            <span>Add Expense</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="general-expenses-cards">
        <div className="general-expenses-card general-expenses-card-green">
          <div className="general-expenses-card-icon general-green-icon">
            <FontAwesomeIcon icon={faArrowUp} />
          </div>
          <div className="general-expenses-card-content">
            <div className="general-expenses-card-label">Uninvoiced</div>
            <div className="general-expenses-card-value">${metrics.uninvoicedAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            <div className="general-expenses-card-sub">{metrics.uninvoicedCount} item{metrics.uninvoicedCount === 1 ? '' : 's'}</div>
          </div>
        </div>

        <div className="general-expenses-card general-expenses-card-red">
          <div className="general-expenses-card-icon general-red-icon">
            <FontAwesomeIcon icon={faArrowUp} />
          </div>
          <div className="general-expenses-card-content">
            <div className="general-expenses-card-label">Total Expenses</div>
            <div className="general-expenses-card-value">${metrics.totalExpenses.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
        </div>

        <div className="general-expenses-card general-expenses-card-blue">
          <div className="general-expenses-card-icon general-blue-icon">
            <FontAwesomeIcon icon={faArrowUp} />
          </div>
          <div className="general-expenses-card-content">
            <div className="general-expenses-card-label">Top Category</div>
            <div className="general-expenses-card-value">{metrics.topCategory}</div>
          </div>
        </div>

        <div className="general-expenses-card general-expenses-card-yellow">
          <div className="general-expenses-card-icon general-yellow-icon">
            <FontAwesomeIcon icon={faClock} />
          </div>
          <div className="general-expenses-card-content">
            <div className="general-expenses-card-label">Invoiced</div>
            <div className="general-expenses-card-value">${metrics.invoicedAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="general-expenses-filters">
        <div className="general-expenses-search">
          <input
            type="text"
            placeholder="Search Expenses..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
            className="general-expenses-search-input"
          />
        </div>
        <div className="general-expenses-filter-dropdowns">
          <select
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
            className="general-expenses-filter-select"
          >
            <option>All types</option>
            <option>General</option>
            <option>Vendor Services</option>
            <option>Fixed Bills</option>
            <option>Operating</option>
          </select>
          <select
            value={dateFilter}
            onChange={(e) => { setDateFilter(e.target.value); setPage(1); }}
            className="general-expenses-filter-select"
          >
            <option>All time</option>
            <option>Last 7 Days</option>
            <option>Last 90 Days</option>
            <option>This Year</option>
          </select>
          <select
            value={invoiceFilter}
            onChange={(e) => { setInvoiceFilter(e.target.value as "all" | "invoiced" | "uninvoiced"); setPage(1); }}
            className="general-expenses-filter-select"
          >
            <option value="all">All statuses</option>
            <option value="invoiced">Invoiced</option>
            <option value="uninvoiced">Uninvoiced</option>
          </select>
        </div>
      </div>

      {/* Expenses Table */}
      {isLoading ? (
        <p style={{ padding: '20px' }}>Loading expenses data...</p>
      ) : (
        <Table
          data={paginatedExpenses}
          rowKey="id"
          columns={columns}
          emptyMessage="No expenses data available"
          pagination={{
            currPage: page,
            onPageChange: (p: number) => setPage(p),
            pageLimit,
            pageSize,
            onPageSizeChange: (s: number) => { setPageSize(s); setPage(1); },
            totalItems: totalFiltered,
            itemLabel: "expenses",
          }}
        />
      )}

      <LogExpenseEntryModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleModalSuccess}
      />

      <ExpensesExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        data={filteredExpenses}
      />

      {showInvoicedPanel && (
        <ExpensesInvoicedPanel
          onClose={() => setShowInvoicedPanel(false)}
        />
      )}

      {/* Edit Modal */}
      {editingItem && (
        <div className="general-billing-modal-overlay" onClick={() => setEditingItem(null)}>
          <div className="general-billing-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Edit {editingItem.type}</h3>
            <div className="general-billing-modal-form">
              <label>
                <span>Description</span>
                <input
                  type="text"
                  value={editFormData.description}
                  onChange={(e) => setEditFormData(p => ({ ...p, description: e.target.value }))}
                />
              </label>
              <label>
                <span>Amount</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editFormData.amount}
                  onChange={(e) => setEditFormData(p => ({ ...p, amount: e.target.value }))}
                />
              </label>
              <label>
                <span>Date</span>
                <input
                  type="date"
                  value={editFormData.date}
                  onChange={(e) => setEditFormData(p => ({ ...p, date: e.target.value }))}
                />
              </label>
            </div>
            <div className="general-billing-modal-actions">
              <button className="general-billing-ghost-btn" onClick={() => setEditingItem(null)} disabled={isSavingEdit}>
                Cancel
              </button>
              <button className="general-billing-primary-btn" onClick={handleSaveEdit} disabled={isSavingEdit}>
                {isSavingEdit ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteItem && (
        <div className="general-billing-modal-overlay" onClick={() => setDeleteItem(null)}>
          <div className="general-billing-modal general-billing-modal-sm" onClick={(e) => e.stopPropagation()}>
            <h3>Delete {deleteItem.type}</h3>
            <p>Are you sure you want to delete this entry? This action cannot be undone.</p>
            <div className="general-billing-modal-detail">
              <span>{deleteItem.description}</span>
              <span>${deleteItem.amount.toFixed(2)}</span>
            </div>
            <div className="general-billing-modal-actions">
              <button className="general-billing-ghost-btn" onClick={() => setDeleteItem(null)} disabled={isDeleting}>
                Cancel
              </button>
              <button className="general-billing-delete-confirm-btn" onClick={handleDelete} disabled={isDeleting}>
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expenses;
